import { useState, type ReactNode } from "react";
import { ExternalLink, Eye, Lock, MessageSquare } from "lucide-react";
import { Dropdown, type DropdownItem } from "@ui";
import { BaseDrawer } from "@viewComponents/profile_utils/drawers/base";
import type { InsitiProfile } from "./index";
import { useContactStatus } from "./contact-status-context";
import { RevealDialog } from "./reveal-dialog";

declare const Turbo: {
  visit: (url: string, options?: { frame?: string }) => void;
  renderStreamMessage: (html: string) => void;
};

interface ProfileActionsDropdownProps {
  profile: InsitiProfile;
  trigger: ReactNode;
  placement?: "bottom-start" | "bottom-end";
  inline?: boolean;
}

export function ProfileActionsDropdown({
  profile,
  trigger,
  placement = "bottom-start",
  inline = false,
}: ProfileActionsDropdownProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [revealOpen, setRevealOpen] = useState(false);
  const status = useContactStatus(profile.user_id);

  const handleSendMessage = () => {
    if (!profile.user_id) return;
    Turbo.visit(`/app/messages/new?recipient_ids[]=${profile.user_id}`, {
      frame: "drawer",
    });
  };

  const handleViewProfile = () => setDrawerOpen(true);

  const handlePrivacy = () => {
    if (!profile.user_id) return;
    Turbo.visit(`/app/contact_privacy/${profile.user_id}`, { frame: "modal" });
  };

  // Reveal/contact affordance mirrors the profiles_researches row menu:
  //   locked   → "Contact privé" (→ privacy modal)
  //   revealed → "Coordonnées affichées" (disabled)
  //   else     → "Afficher les coordonnées" (→ reveal dialog)
  const revealItem: DropdownItem = status?.privacy_locked
    ? {
        label: "Contact privé",
        icon: <Lock className="w-4 h-4" />,
        onClick: handlePrivacy,
      }
    : status?.revealed
      ? {
          label: "Coordonnées affichées",
          icon: <Eye className="w-4 h-4" />,
          disabled: true,
        }
      : {
          label: "Afficher les coordonnées",
          icon: <Eye className="w-4 h-4" />,
          onClick: () => setRevealOpen(true),
          disabled: !profile.user_id,
        };

  const dropdownItems: DropdownItem[] = [
    revealItem,
    {
      label: "Envoyer un message",
      icon: <MessageSquare className="w-4 h-4" />,
      onClick: handleSendMessage,
      disabled: !profile.user_id,
    },
    {
      label: "Voir le profil",
      icon: <Eye className="w-4 h-4" />,
      onClick: handleViewProfile,
    },
  ];

  return (
    <>
      <Dropdown
        trigger={trigger}
        items={dropdownItems}
        placement={placement}
        inline={inline}
      />
      {revealOpen && profile.user_id && (
        <RevealDialog
          open={revealOpen}
          onClose={() => setRevealOpen(false)}
          userIds={[profile.user_id]}
        />
      )}
      {drawerOpen && (
        <BaseDrawer
          entity={{ id: profile.id }}
          entityType={profile.type}
          onClose={() => setDrawerOpen(false)}
        />
      )}
    </>
  );
}
